import {clsx} from 'clsx';
import type {UseMultipleSelectionGetSelectedItemReturnValue} from 'downshift';
import type {KeyboardEvent, MouseEvent, PropsWithChildren} from 'react';
import {memo, useCallback} from 'react';

import {Icon} from '../../../atoms/icon/index.js';
import * as styles from './style.module.scss';

type SelectedItemProps = {
  text: string;
  title?: string;
  onRemove?: () => void;
} & UseMultipleSelectionGetSelectedItemReturnValue;

const SelectedItems = ({
  children,
  className,
  expand = false,
}: PropsWithChildren<{className?: string; expand?: boolean}>) => {
  return (
    <div
      className={clsx(
        styles.selectedItems,
        expand && styles.expandedSelectedItems,
        className
      )}
    >
      {children}
    </div>
  );
};

SelectedItems.displayName = 'SelectedItems';

const SelectedItem = memo(function SelectedItem({
  text,
  onRemove,
  ...props
}: SelectedItemProps) {
  const handleOnRemove = useCallback(
    (evt: KeyboardEvent | MouseEvent) => {
      evt.stopPropagation();
      onRemove?.();
    },
    [onRemove]
  );

  return (
    <span {...props} className={styles.selectedItem}>
      <span
        className={styles.selectedItemText}
        role="option"
        aria-selected="true"
        tabIndex={-1}
      >
        {text}
      </span>
      {onRemove && (
        <button
          aria-label={`Remove ${text}`}
          type="button"
          onKeyDown={evt => {
            if (evt.key === 'Enter') {
              handleOnRemove(evt);
            }
          }}
          onClick={handleOnRemove}
        >
          <Icon name="close" size={7} color="primary" />
        </button>
      )}
    </span>
  );
});

export {SelectedItems, SelectedItem};
